"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ElevateYourBrandWithSamBrandingAndMarketing() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  const [heroOpacity, setHeroOpacity] = useState(1);
  const {
    ref: cardRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      const fadeStart = 0;
      const fadeEnd = 600;
      const opacity = Math.max(0, 1 - (currentScrollY - fadeStart) / (fadeEnd - fadeStart));
      setHeroOpacity(opacity);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      opacity: number;
    }
    const particles: Particle[] = [];
    const particleCount = 40;
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        radius: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.2
      });
    }
    let animationId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 215, 0, ${p.opacity})`;
        ctx.fill();
      });
      animationId = requestAnimationFrame(animate);
    };
    if (canvas.width > 0 && canvas.height > 0) {
      animate();
    }
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);
  return <section ref={heroRef} className="relative min-h-screen w-full overflow-hidden bg-dark-background" style={{
    opacity: heroOpacity
  }}>
      {}
      <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1438489/img/sam_branding_hero_cityscape.png)]" style={{
      transform: `translateY(${scrollY * 0.5}px)`,
      willChange: "transform"
    }} />

      {}
      <div className="absolute inset-0 bg-primary/60 backdrop-blur-sm" />

      {}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{
      mixBlendMode: "screen"
    }} />

      {}
      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-16">
        <Card ref={cardRef} className={`glass-effect max-w-3xl w-full transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} hover:shadow-strong hover:-translate-y-1`}>
          <CardContent className="space-y-6 text-center p-8 md:p-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-500/20 border border-gray-500/40 text-gray-500 text-sm font-semibold mb-4 shadow-soft">
              <Sparkles className="size-4" />
              <span className="text-black">Transform Your Brand Today</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight">
              Elevate Your Brand with{" "}
              <span className="text-accent-dark">Sam Branding & Marketing</span>
            </h1>

            <p className="text-lg md:text-xl text-foreground max-w-2xl mx-auto leading-relaxed">
              All-in-one branding and marketing solutions tailored to your
              business. From digital presence to creative design, we bring your
              vision to life with measurable results.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link to="/ServicesPage">
                <Button size="lg" className="btn-primary group gap-2 min-w-[200px] shadow-medium hover:shadow-strong">
                  Explore Our Services
                  <ArrowRight className="size-5 transition-transform duration-[var(--duration-fast)] group-hover:translate-x-1" />
                </Button>
              </Link>

              <Link to="/ContactPage">
                <Button size="lg" variant="outline" className="min-w-[200px] bg-background/20 text-foreground border-foreground-dark/30 hover:bg-background/40 hover:border-accent hover:text-accent-dark backdrop-blur-sm focus-ring shadow-soft hover:shadow-medium">
                  Get in Touch
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-8 pt-6 text-sm text-foreground">
              <div className="flex items-center gap-2">
                <div className="size-2 rounded-full bg-accent animate-pulse shadow-soft" />
                <span className="font-medium">12 Premium Services</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="size-2 rounded-full bg-secondary animate-pulse shadow-soft" />
                <span className="font-medium">Fast Turnaround</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-foreground transition-opacity duration-[var(--duration-medium)]" style={{
      opacity: heroOpacity
    }}>
        <span className="text-xs font-medium uppercase tracking-wider">
          Scroll to discover
        </span>
        <div className="w-6 h-10 rounded-full border-2 border-foreground-dark/30 flex items-start justify-center p-2">
          <div className="w-1.5 h-2 bg-accent rounded-full animate-bounce" />
        </div>
      </div>
    </section>;
}